/**
 * career-werte-view — the Kompass »Meine Werte« read-only display (route
 * `/compass/werte`), the deferred V5 §8 self-view.
 *
 * A calm, non-editing mirror of what Workshop 1 produced: the committed 1·3·4 value
 * pyramid (the shared value-card faces + done-pyramid layout) and the four confirmed
 * statements (»Mein Warum« / »Meine Positionierung« / »Meine Arbeitsphilosophie« /
 * »Meine Vision«). Editing lives in the workshop track — this is the durable read.
 * Honest empty state: when nothing is committed yet, it points to Workshop ①. Data via
 * getValueGame (pyramid) + getWerteSektion (statements), both auth-only reads.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Router,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { html, nothing, type TemplateResult } from "@platform/web-kit/render"
import type { PersonalStatements, PersonalValueEntry, SchwartzProfile, ValuePyramid } from "@career/definition-v2"
import { schwartzWheel } from "../lib/schwartz-wheel"
import { valuePyramid, valueStatements } from "../lib/werte-summary"
import { RouterToken, I18nToken, apiGet } from "../services"
import type { CareerI18n } from "../../i18n"
import { nextStep } from "../lib/next-step"
import { forwardDecl, toNextStep } from "../golden-path"

interface ValueGameView {
  pyramid: ValuePyramid | null
}
interface SektionView {
  values: PersonalValueEntry[]
  statements: PersonalStatements | null
  produced: boolean
}
/** The slice of the interview view this self-view reads — just the prognosed profile. */
interface InterviewProfileView {
  schwartzProfile: SchwartzProfile | null
}

export class WerteViewVm implements ViewModel {
  readonly game: Signal<ValueGameView | null> = signal<ValueGameView | null>(null)
  readonly sektion: Signal<SektionView | null> = signal<SektionView | null>(null)
  readonly profile: Signal<SchwartzProfile | null> = signal<SchwartzProfile | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]

  constructor(private readonly router: Router, i18n: CareerI18n) {
    this.t = i18n.t
    this.renderDeps = [this.game, this.sektion, this.profile, this.loaded, this.error]
  }

  attach(): () => void {
    void this.refresh()
    return () => {}
  }

  private async refresh(): Promise<void> {
    // Independent reads: a missing/failed sektion still lets the pyramid render (and
    // vice-versa) — one thin fetch never blanks the whole self-view. The interview view
    // carries the prognosed Schwartz wheel (§5c).
    const [game, sektion, interview] = await Promise.all([
      apiGet<ValueGameView>("/api/workshops/values/cardgame").catch(() => null),
      apiGet<SektionView>("/api/workshops/values/sektion").catch(() => null),
      apiGet<InterviewProfileView>("/api/workshops/values/interview").catch(() => null),
    ])
    this.game.set(game)
    this.sektion.set(sektion)
    this.profile.set(interview?.schwartzProfile ?? null)
    this.loaded.set(true)
  }

  go(url: string): void {
    void this.router.navigate(url)
  }
}

export class CareerWerteView extends KitElement<WerteViewVm> {
  protected createViewModel(ctx: KitContext): WerteViewVm {
    return new WerteViewVm(ctx.inject(RouterToken), ctx.inject(I18nToken))
  }

  private empty(vm: WerteViewVm): TemplateResult {
    return html`<div class="werte-empty">
      <p class="surface-lead" lang="de">${vm.t("werte.view.empty")}</p>
      <button class="primary" data-act="to-workshop" lang="de">${vm.t("werte.view.toWorkshop")}</button>
    </div>`
  }

  protected render(vm: WerteViewVm): TemplateResult {
    const pyramid = vm.game.value?.pyramid ?? null
    const sektion = vm.sektion.value
    const profile = vm.profile.value
    const hasWheel = Boolean(profile && profile.sectors.length > 0)
    const hasStatements = Boolean(sektion?.produced && sektion.statements)
    const anything = Boolean(pyramid) || hasStatements || hasWheel
    return html`
      <section class="surface werte-view" aria-labelledby="werte-title">
        <p class="eyebrow" lang="de">${vm.t("werte.view.kicker")}</p>
        <h1 id="werte-title" class="surface-title" lang="de">${vm.t("surface.werte.title")}</h1>
        <p class="surface-lead" lang="de">${vm.t("werte.view.lead")}</p>
        <a class="werte-canon-link" href="/workshops" data-act="nav" data-url="/workshops" lang="de">${vm.t("werte.view.canonLink")}</a>
        ${vm.error.value ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: vm.error.value })}</p>` : nothing}
        ${!vm.loaded.value
          ? nothing
          : !anything
            ? this.empty(vm)
            : html`
                ${pyramid
                  ? html`<section class="werte-block">
                      <h2 class="surface-section-title" lang="de">${vm.t("werte.view.pyramidTitle")}</h2>
                      ${valuePyramid(vm.t, pyramid)}
                    </section>`
                  : nothing}
                ${hasWheel && profile
                  ? html`<section class="werte-block">
                      <h2 class="surface-section-title" lang="de">${vm.t("werte.view.wheelTitle")}</h2>
                      ${schwartzWheel(profile)}
                      ${profile.insight ? html`<p class="iv-reward-insight" lang="de">${profile.insight}</p>` : nothing}
                    </section>`
                  : nothing}
                ${hasStatements && sektion?.statements
                  ? html`<section class="werte-block">
                      <h2 class="surface-section-title" lang="de">${vm.t("werte.view.statementsTitle")}</h2>
                      ${valueStatements(vm.t, sektion.statements, "werte.view.stmtGap")}
                    </section>`
                  : nothing}
                ${this.goldenNext(vm)}`}
      </section>
    `
  }

  /** The golden-path branch forward (D4): from »Meine Werte« onward to the profile. */
  private goldenNext(vm: WerteViewVm): TemplateResult | typeof nothing {
    const decl = forwardDecl("career-werte-view")
    if (!decl) return nothing
    return nextStep(vm.t("next.eyebrow"), toNextStep(decl, vm.t(decl.labelKey)))
  }

  protected override actions(vm: WerteViewVm): ActionMap {
    return {
      "to-workshop": () => vm.go("/workshops/values"),
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
    }
  }
}
